import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Check, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { useEnquiry } from "../components/EnquiryModal";
import { KITCHENS } from "../data/kitchens";
import { waLink, SITE } from "../lib/site";
import { WaIcon } from "../components/icons";

const DetailBlock = ({ title, items, testid }) => (
  <div data-testid={testid} className="h-full rounded-2xl border border-ink/10 bg-white p-7">
    <h3 className="eyebrow text-golddim">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-sm leading-relaxed text-ink/75">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={3} />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

export default function KitchenDetail() {
  const { slug } = useParams();
  const { openEnquiry } = useEnquiry();
  const kitchen = KITCHENS.find((k) => k.slug === slug);

  if (!kitchen) {
    return (
      <div className="grid min-h-[70vh] place-items-center bg-ivory px-6 pt-24 text-center">
        <div>
          <h1 className="font-display text-3xl text-ink">Kitchen not found.</h1>
          <Link
            to="/kitchens"
            data-testid="back-to-kitchens"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-royal px-7 py-3.5 text-sm font-semibold text-ivory"
          >
            <ChevronLeft className="h-4 w-4" /> Back to all kitchens
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${kitchen.name} — ${kitchen.category} | Paramount Kitchen Kangra`}
        description={`${kitchen.description} Premium ${kitchen.category.toLowerCase()} manufactured in Kangra District, Himachal Pradesh by Paramount Kitchen.`}
      />

      <section className="bg-royal pb-16 pt-32 text-ivory md:pb-24 md:pt-44">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2">
          <div>
            <Reveal>
              <Link
                to="/kitchens"
                data-testid="kitchen-detail-back"
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-ivory/60 transition-colors hover:text-gold"
              >
                <ChevronLeft className="h-4 w-4" /> All Kitchens
              </Link>
              <p className="eyebrow mt-8 text-gold">{kitchen.category}</p>
              <h1 className="text-balance mt-4 font-display text-4xl font-medium leading-tight md:text-6xl">
                {kitchen.name}
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-ivory/70">
                {kitchen.description}
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <button
                  data-testid="enquire-for-this-kitchen"
                  onClick={() =>
                    openEnquiry({ source: "kitchen_detail", kitchenType: kitchen.name })
                  }
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-royal shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-golddim hover:text-ivory"
                >
                  Enquire for This Kitchen
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <a
                  data-testid="kitchen-detail-whatsapp"
                  href={waLink(`Hi Paramount Kitchen! I am interested in "${kitchen.name}" (${kitchen.category}). Please share more details.`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-8 py-4 text-sm font-semibold text-ivory backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
                >
                  <WaIcon className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <img
              src={kitchen.image.replace("w=1200", "w=1600")}
              alt={`${kitchen.name} — ${kitchen.category} by Paramount Kitchen, Kangra, Himachal Pradesh`}
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-medium text-ink md:text-4xl">
              Plan &amp; <span className="serif-accent text-golddim">possibilities.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Reveal>
              <div data-testid="kitchen-detail-layout" className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                <h3 className="eyebrow text-golddim">Layout</h3>
                <p className="mt-5 text-lg font-medium leading-relaxed text-ink">{kitchen.layout}</p>
              </div>
            </Reveal>
            <Reveal delay={0.07}>
              <div data-testid="kitchen-detail-recommended" className="h-full rounded-2xl border border-ink/10 bg-white p-7">
                <h3 className="eyebrow text-golddim">Recommended Use</h3>
                <p className="mt-5 text-lg font-medium leading-relaxed text-ink">
                  {kitchen.recommendedUse}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <DetailBlock title="Storage Possibilities" items={kitchen.storage} testid="kitchen-detail-storage" />
            </Reveal>
            <Reveal delay={0.05}>
              <DetailBlock title="Finish Options" items={kitchen.finishes} testid="kitchen-detail-finishes" />
            </Reveal>
            <Reveal delay={0.12}>
              <DetailBlock title="Hardware Possibilities" items={kitchen.hardware} testid="kitchen-detail-hardware" />
            </Reveal>
            <Reveal delay={0.19}>
              <div className="flex h-full flex-col justify-between rounded-2xl bg-royal p-7 text-ivory">
                <div>
                  <h3 className="eyebrow text-gold">Built the Paramount way</h3>
                  <p className="mt-5 text-sm leading-relaxed text-ivory/70">
                    Every design in this collection is manufactured on our engineered carcass —
                    laminated plywood, hydraulic cold pressing, precision cutting, edge banding and
                    stainless-steel structural joining.
                  </p>
                </div>
                <Link
                  to="/carcass"
                  data-testid="kitchen-detail-construction-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:underline"
                >
                  See the construction <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-16">
            <h2 className="font-display text-3xl font-medium text-ink md:text-4xl">
              A closer <span className="serif-accent text-golddim">look.</span>
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {kitchen.gallery.map((g, i) => (
                <img
                  key={i}
                  src={g}
                  alt={`${kitchen.name} view ${i + 1} — ${kitchen.category}, Paramount Kitchen`}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover shadow-sm transition-transform duration-500 hover:scale-[1.02]"
                />
              ))}
            </div>
            <p className="mt-4 text-xs text-ink/40">
              Representative imagery — replaceable with actual Paramount Kitchen project photographs.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
